import { useAtomValue } from 'jotai';
import { useEffect } from 'react';

import { useTrackEvent } from '../../segment/segment';
import { EventTypeEnum } from '../../segment/types';
import { ParticipantStatus } from '../../types';
import { selectedClientInfoAtom } from '../state';
import { useGetMeasuresByPatientShortId } from '../useGetMeasuresByPatientShortId';
import { ClientInformationDrawerBodyWrapper as ClientInformationDrawerBody } from './ClientInformationDrawerBody';
import { ClientInformationDrawerFooterWrapper as ClientInformationDrawerFooter } from './ClientInformationDrawerFooter';
import { ClientInformationDrawerHeaderWrapper as ClientInformationDrawerHeader } from './ClientInformationDrawerHeader';
import { ClientInformationSchedulingDrawerFooterWrapper as ClientInformationSchedulingDrawerFooter } from './ClientInformationSchedulingDrawerFooter';

type ClientInformationDrawerProps = {
  visitorName?: string;
  patientShortId?: string;
  isEmpty: boolean;
  visitorStatus?: ParticipantStatus;
  visitorUuid?: string;
  selectedPatientShortId: string | undefined;
};

export function ClientInformationDrawer({
  isEmpty,
  visitorName,
  visitorStatus,
  visitorUuid,
  selectedPatientShortId,
}: ClientInformationDrawerProps) {
  const isScheduledClient = visitorStatus === ParticipantStatus.SCHEDULED;
  return (
    <>
      <ClientInformationDrawerHeader
        visitorName={visitorName}
        visitorStatus={visitorStatus}
        selectedPatientShortId={selectedPatientShortId || ''}
      />
      <ClientInformationDrawerBody isEmpty={isEmpty} />
      {isScheduledClient && <ClientInformationSchedulingDrawerFooter />}
      {!isScheduledClient && (
        <ClientInformationDrawerFooter
          visitorStatus={visitorStatus}
          visitorUuid={visitorUuid || ''}
          visitorName={visitorName || ''}
          patientShortId={selectedPatientShortId || ''}
        />
      )}
    </>
  );
}

export function ClientInformationDrawerWrapper() {
  const selectedClient = useAtomValue(selectedClientInfoAtom);
  // Ensures patient measures are fetched when a provider opens the drawer via the upcoming appointment list
  useGetMeasuresByPatientShortId(selectedClient?.patientShortId);

  const { userViewed } = useTrackEvent();

  useEffect(function trackClientInfoDrawerViewed() {
    userViewed(EventTypeEnum.CLIENT_INFO, {
      patientShortId: selectedClient?.patientShortId ?? '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ClientInformationDrawer
      selectedPatientShortId={selectedClient?.patientShortId}
      visitorName={selectedClient?.name ?? 'Client Information'}
      visitorStatus={selectedClient?.status}
      visitorUuid={selectedClient?.visitorUuid}
      isEmpty={!selectedClient}
    />
  );
}
